import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Play, Pause, Volume2, VolumeX, ExternalLink } from "lucide-react";
import { api, resolveAudio } from "@/lib/api";

/**
 * Compact embeddable player. Self-contained — does NOT use the global PlayerProvider.
 * Designed to be loaded inside an <iframe> at ~340x110.
 */
export default function EmbedPlayer() {
  const { id } = useParams();
  const [station, setStation] = useState(null);
  const [current, setCurrent] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [vol, setVol] = useState(0.85);
  const audioRef = useRef(null);
  const lastTrackRef = useRef(null);
  const listenerIdRef = useRef("emb_" + Math.random().toString(36).slice(2, 12));

  const syncNow = useCallback(async () => {
    try {
      const { data } = await api.get(`/stations/${id}/now`);
      if (!data.current) return;
      const audio = audioRef.current;
      if (!audio) return;
      const isNew = lastTrackRef.current !== data.current.track_id;
      if (isNew) {
        lastTrackRef.current = data.current.track_id;
        audio.src = resolveAudio(data.current.audio_url);
        audio.currentTime = data.offset || 0;
      } else if (Math.abs((audio.currentTime || 0) - data.offset) > 3) {
        audio.currentTime = data.offset;
      }
      setCurrent(data.current);
    } catch (_) {}
  }, [id]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/stations/${id}`);
        setStation(r.data.station);
      } catch (_) {}
    })();
  }, [id]);

  useEffect(() => {
    syncNow();
    const t = setInterval(syncNow, 10000);
    return () => clearInterval(t);
  }, [syncNow]);

  // Heartbeat so listeners on embeds count in analytics — but skip in dashboard preview mode
  useEffect(() => {
    if (!station) return;
    const isPreview = typeof window !== "undefined" && window.location.search.includes("preview=1");
    if (isPreview) return;
    const send = () => api.post(`/stations/${id}/heartbeat`, { listener_id: listenerIdRef.current }).catch(() => {});
    send();
    const t = setInterval(send, 30000);
    return () => clearInterval(t);
  }, [id, station]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = vol;
      audioRef.current.muted = muted;
    }
  }, [vol, muted]);

  const toggle = async () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      await syncNow();
      try { await a.play(); setIsPlaying(true); } catch (_) {}
    } else { a.pause(); setIsPlaying(false); }
  };

  if (!station) {
    return <div style={{ background: "#050505", color: "#52525B", height: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "monospace", fontSize: 11, letterSpacing: 2, textTransform: "uppercase" }}>// Tuning…</div>;
  }

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh" }}>
    <div className="grain" style={{
      background: "linear-gradient(135deg, #0a0a0a 0%, #121212 100%)",
      flex: 1, color: "#F8FAFC", padding: 10,
      fontFamily: "'Work Sans', system-ui, sans-serif",
      borderRadius: "12px 12px 0 0", border: "1px solid rgba(255,255,255,0.08)", borderBottom: "none",
      display: "flex", alignItems: "center", gap: 12
    }}>
      <div style={{ width: 64, height: 64, borderRadius: 8, overflow: "hidden", background: "#1a1a1a", flexShrink: 0, position: "relative" }}>
        {(current?.cover_url || station.cover_url)
          ? <img src={current?.cover_url || station.cover_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          : <div style={{ width: "100%", height: "100%", background: "linear-gradient(135deg,#27272A,#0a0a0a)" }} />}
        <div style={{ position: "absolute", inset: 0, boxShadow: "inset 0 0 0 1px rgba(245,158,11,0.35)" }} />
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 9, letterSpacing: 2.5, textTransform: "uppercase", color: "#F59E0B", fontFamily: "'JetBrains Mono', monospace" }}>
          <span className="live-dot" style={{ width: 6, height: 6 }} /> LIVE · {station.name}
        </div>
        <div style={{ marginTop: 4, fontSize: 14, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} data-testid="embed-track-title">
          {current ? `${current.title}` : "Tuning…"}
        </div>
        <div style={{ fontSize: 11, color: "#94A3B8", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {current?.artist || "—"}
        </div>
      </div>

      <button onClick={toggle} aria-label="Play / Pause" data-testid="embed-play-button" style={{
        width: 42, height: 42, borderRadius: "50%",
        background: "#F59E0B", color: "#0a0a0a", border: "none",
        display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
        boxShadow: "0 8px 24px -8px rgba(245,158,11,0.55)"
      }}>
        {isPlaying ? <Pause size={18} /> : <Play size={18} style={{ marginLeft: 2 }} />}
      </button>

      <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "center" }}>
        <button onClick={() => setMuted(!muted)} style={{ background: "transparent", border: "none", color: "#94A3B8", cursor: "pointer" }} aria-label="Mute">
          {muted || vol === 0 ? <VolumeX size={14} /> : <Volume2 size={14} />}
        </button>
        <input
          type="range" min={0} max={1} step={0.01}
          value={muted ? 0 : vol}
          onChange={(e) => { setVol(parseFloat(e.target.value)); if (muted) setMuted(false); }}
          style={{ width: 50, accentColor: "#F59E0B" }}
          aria-label="Volume"
        />
      </div>

      <a href={`${origin}/s/${id}`} target="_blank" rel="noreferrer" title="Open on Fliboe" style={{ color: "#52525B", display: "flex" }}>
        <ExternalLink size={14} />
      </a>

      <audio ref={audioRef} preload="auto" onEnded={syncNow} />
    </div>

    {/* WATERMARKED WIDGET INVASION — subtle backlink turning every embed into a
        free billboard for the SaaS. UTM tags so we can attribute traffic. */}
    <a
      href={`${origin}/?utm_source=embed&utm_medium=widget&utm_campaign=powered_by&utm_content=${id}`}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="embed-watermark"
      style={{
        display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
        height: 24, background: "#050505",
        borderRadius: "0 0 12px 12px",
        border: "1px solid rgba(255,255,255,0.08)", borderTop: "1px solid rgba(255,255,255,0.05)",
        color: "rgba(255,255,255,0.55)", textDecoration: "none",
        fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
        letterSpacing: 0.8, whiteSpace: "nowrap", overflow: "hidden",
        textOverflow: "ellipsis", padding: "0 8px",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.color = "#F59E0B"; }}
      onMouseLeave={(e) => { e.currentTarget.style.color = "rgba(255,255,255,0.55)"; }}
      title="Launch your own internet radio station on Fliboe FM"
    >
      <span style={{ opacity: 0.9 }}>📻</span>
      <span>Powered by <b style={{ color: "#F59E0B" }}>Fliboe FM</b> — Launch your own station in 60 seconds</span>
    </a>
    </div>
  );
}
